import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Trophy, TrendingUp, Users, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';

interface GameResult {
  id: string;
  game_id: string;
  home_team_score: number;
  away_team_score: number;
  winning_team: string;
  final_status: string;
  notes?: string;
  created_at: string;
  game: {
    id: string;
    game_name: string;
    game_date: string;
    home_team_label: string;
    away_team_label: string;
    sport_id?: number;
  };
}

interface LeaderboardEntry {
  entity_id: string;
  entity_type: string;
  sport_id?: number;
  season_year: number;
  games_played: number;
  games_won: number;
  games_lost: number;
  games_drawn: number;
  win_rate: number;
  total_points: number;
  rank_position?: number;
}

export default function GameStats() {
  const [gameResults, setGameResults] = useState<GameResult[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [sports, setSports] = useState<any[]>([]);
  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [selectedSeason, setSelectedSeason] = useState<string>(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [selectedSport, selectedSeason]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadGameResults(),
        loadSports(),
        loadLeaderboard()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load game statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadGameResults = async () => {
    try {
      const { data, error } = await supabase
        .from('game_results')
        .select(`
          *,
          game:games(
            id,
            game_name,
            game_date,
            home_team_label,
            away_team_label,
            sport_id
          )
        `)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setGameResults(data || []);
    } catch (error) {
      console.error('Error loading game results:', error);
    }
  };

  const loadSports = async () => {
    try {
      const { data, error } = await supabase
        .from('available_sports')
        .select('*')
        .order('sport_name');

      if (error) throw error;
      setSports(data || []);
    } catch (error) {
      console.error('Error loading sports:', error);
    }
  };

  const loadLeaderboard = async () => {
    try {
      let query = supabase
        .from('leaderboard')
        .select('*')
        .eq('season_year', parseInt(selectedSeason))
        .order('total_points', { ascending: false })
        .limit(50);

      if (selectedSport !== 'all') {
        query = query.eq('sport_id', parseInt(selectedSport));
      }

      const { data, error } = await query;

      if (error) throw error;
      setLeaderboard(data || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }
  };

  const getSportName = (sportId?: number) => {
    if (!sportId) return 'N/A';
    const sport = sports.find(s => s.id === sportId);
    return sport?.sport_name || 'Unknown Sport';
  };

  const getResultBadge = (result: GameResult) => {
    if (result.final_status === 'forfeited') {
      return <Badge variant="destructive">Forfeited</Badge>;
    }
    if (result.final_status === 'cancelled') {
      return <Badge variant="outline">Cancelled</Badge>;
    }
    if (result.winning_team === 'draw') {
      return <Badge variant="secondary">Draw</Badge>;
    }
    return <Badge variant="default">Completed</Badge>;
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-background">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </main>
        
        {!isMobile && <Footer />}
        {isMobile && <MobileBottomNav />}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow ${isMobile ? 'pt-16 pb-16' : 'pt-32'}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">Game Statistics</h1>
          <p className="text-muted-foreground">View game results, leaderboards, and performance analytics</p>
        </div>

        <Tabs defaultValue="results" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="results">Recent Results</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="results" className="space-y-6">
            <div className="grid gap-4">
              {gameResults.map((result) => (
                <Card key={result.id}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{result.game.game_name}</CardTitle>
                        <CardDescription>
                          {getSportName(result.game.sport_id)} • {format(new Date(result.game.game_date), 'MMM d, yyyy')}
                        </CardDescription>
                      </div>
                      {getResultBadge(result)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 items-center">
                      <div className="text-center">
                        <div className="font-semibold text-lg">{result.game.home_team_label}</div>
                        <div className="text-2xl font-bold text-primary">{result.home_team_score}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-muted-foreground text-sm">Final Score</div>
                        <div className="text-lg font-medium">vs</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-lg">{result.game.away_team_label}</div>
                        <div className="text-2xl font-bold text-primary">{result.away_team_score}</div>
                      </div>
                    </div>
                    {result.notes && (
                      <div className="mt-4 p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">{result.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {gameResults.length === 0 && (
              <div className="text-center py-12">
                <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-muted-foreground mb-2">No game results yet</h3>
                <p className="text-muted-foreground">Game results will appear here once games are completed</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="leaderboard" className="space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <Select value={selectedSport} onValueChange={setSelectedSport}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="All Sports" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sports</SelectItem>
                  {sports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id.toString()}>
                      {sport.sport_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue placeholder="Season" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2023">2023</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-4">
              {leaderboard.map((entry, index) => (
                <Card key={`${entry.entity_id}-${entry.entity_type}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-semibold capitalize">
                            {entry.entity_type} #{entry.entity_id.slice(-6)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {entry.games_played} games played
                          </div>
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <div className="text-2xl font-bold text-primary">{entry.total_points}</div>
                        <div className="text-sm text-muted-foreground">
                          {entry.games_won}W-{entry.games_lost}L-{entry.games_drawn}D
                        </div>
                        <div className="text-sm font-medium">
                          {(entry.win_rate * 100).toFixed(1)}% win rate
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {leaderboard.length === 0 && (
              <div className="text-center py-12">
                <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-muted-foreground mb-2">No leaderboard data</h3>
                <p className="text-muted-foreground">Rankings will appear here once games are completed</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Games</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{gameResults.length}</div>
                  <p className="text-xs text-muted-foreground">Games completed this season</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Players</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{leaderboard.filter(l => l.entity_type === 'player').length}</div>
                  <p className="text-xs text-muted-foreground">Players with recorded stats</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Score</CardTitle>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {gameResults.length > 0 
                      ? Math.round(
                          gameResults.reduce((sum, r) => sum + r.home_team_score + r.away_team_score, 0) / 
                          (gameResults.length * 2)
                        )
                      : 0
                    }
                  </div>
                  <p className="text-xs text-muted-foreground">Average points per team</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Coming Soon</CardTitle>
                <CardDescription>Advanced analytics and insights will be available here</CardDescription>
              </CardHeader>
              <CardContent className="text-center py-12">
                <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Detailed performance analytics, trend analysis, and statistical insights
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
}